/**
 * useUserWithdrawData Hook
 * 탈퇴 요청 관리 데이터 훅
 *
 * @description
 * - 탈퇴 요청 목록 조회 (필터 지원)
 * - 탈퇴 요청 승인 처리
 * - 계약 상태/탈퇴 요청 상태 코드 목록 조회
 * - useRef를 사용한 중복 API 호출 방지
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { userWithdrawApi, ApproveWithdrawalRequestDto } from '../api/userWithdrawApi';
import {
    WithdrawRequest,
    withdrawRequestListFromDto
} from '../types/userWithdraw.types';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { CommonCodeDto } from '@/shared/types/commonCode.types';

/**
 * 탈퇴 요청 관리 데이터 훅
 * @returns {Object} 탈퇴 요청 관련 상태 및 함수
 * @property {boolean} loading - 로딩 상태
 * @property {WithdrawRequest[]} withdrawRequests - 탈퇴 요청 목록
 * @property {CommonCodeDto[]} contStatusCodes - 계약 상태 코드 목록
 * @property {CommonCodeDto[]} withdrawalStatusCodes - 탈퇴 요청 상태 코드 목록
 * @property {Function} fetchWithdrawRequests - 탈퇴 요청 목록 조회 함수
 * @property {Function} approveWithdrawal - 탈퇴 요청 승인 함수
 * @property {Function} refreshWithdrawRequests - 탈퇴 요청 목록 새로고침 함수
 */
export const useUserWithdrawData = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [withdrawRequests, setWithdrawRequests] = useState<WithdrawRequest[]>([]);
    const [contStatusCodes, setContStatusCodes] = useState<CommonCodeDto[]>([]);
    const [withdrawalStatusCodes, setWithdrawalStatusCodes] = useState<CommonCodeDto[]>([]);
    const hasInitialized = useRef(false);
    const hasFetchedContStatusCodes = useRef(false);
    const hasFetchedWithdrawalStatusCodes = useRef(false);

    /**
     * 탈퇴 요청 목록 조회
     */
    const fetchWithdrawRequests = useCallback(async (params?: {
        riderName?: string;
        suppName?: string;
        contStatus?: string;
        withdrawalStatusCd?: string;
    }) => {
        try {
            setLoading(true);
            const response = await userWithdrawApi.getWithdrawRequestList(params);
            if (response) {
                const requestList = withdrawRequestListFromDto(response);
                setWithdrawRequests(requestList || []);
            } else {
                setWithdrawRequests([]);
            }
        } catch {
            showToast('탈퇴 요청 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setWithdrawRequests([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // 계약 상태 코드 목록 조회
    useEffect(() => {
        if (hasFetchedContStatusCodes.current) return;

        let isCancelled = false;

        const fetchContStatusCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('CONT_STATUS');
                if (!isCancelled) {
                    setContStatusCodes(response.codes || []);
                    hasFetchedContStatusCodes.current = true;
                }
            } catch {
                // 에러는 무시 (취소된 경우 포함)
            }
        };

        fetchContStatusCodes();
        return () => {
            isCancelled = true;
        };
    }, []);

    // 탈퇴 요청 상태 코드 목록 조회
    useEffect(() => {
        if (hasFetchedWithdrawalStatusCodes.current) return;

        let isCancelled = false;

        const fetchWithdrawalStatusCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('MEMBER_WITHDRAWAL_STATUS_CD');
                if (!isCancelled) {
                    setWithdrawalStatusCodes(response.codes || []);
                    hasFetchedWithdrawalStatusCodes.current = true;
                }
            } catch {
                // 에러는 무시 (취소된 경우 포함)
            }
        };

        fetchWithdrawalStatusCodes();
        return () => {
            isCancelled = true;
        };
    }, []);

    // 초기 데이터 로드
    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            fetchWithdrawRequests().catch(() => {
                // 에러는 훅에서 처리
            });
        }
    }, [fetchWithdrawRequests]);

    /**
     * 탈퇴 요청 승인
     */
    const approveWithdrawal = useCallback(async (requests: ApproveWithdrawalRequestDto[]) => {
        try {
            setLoading(true);
            const response = await userWithdrawApi.approveWithdrawal(requests);
            if (response && response.length > 0) {
                // 모든 요청이 성공했는지 확인
                const allSuccess = response.every(item => item.success);
                const failedItems = response.filter(item => !item.success);
                
                if (allSuccess) {
                    showToast('탈퇴 요청이 승인되었습니다.', { type: ToastType.SUCCESS });
                } else {
                    // 일부 실패한 경우
                    const failedMessages = failedItems.map(item => item.message).join(', ');
                    showToast(`일부 탈퇴 요청 승인에 실패했습니다: ${failedMessages}`, { type: ToastType.ERROR });
                }
                await fetchWithdrawRequests();
                return response;
            } else {
                showToast('탈퇴 요청 승인에 실패했습니다.', { type: ToastType.ERROR });
                return null;
            }
        } catch {
            showToast('탈퇴 요청 승인에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchWithdrawRequests]);

    return {
        loading,
        withdrawRequests,
        contStatusCodes,
        withdrawalStatusCodes,
        fetchWithdrawRequests,
        approveWithdrawal,
        refreshWithdrawRequests: fetchWithdrawRequests
    };
};

